import type { HttpError } from '@/types/http';

/**
 * Type guard for HttpError.
 * Matches the HttpError class and duck-typed objects with the same shape.
 */
function isHttpErrorInstance(error: unknown): error is HttpError {
  return (
    error != null &&
    typeof error === 'object' &&
    'status' in error &&
    'name' in error &&
    (error as HttpError).name === 'HttpError'
  );
}

/**
 * Extract HTTP status code from an error.
 * Works with both AxiosError (current) and HttpError (future).
 * Returns undefined for non-HTTP errors (network errors, cancel errors, etc.)
 */
export function getHttpErrorStatus(error: unknown): number | undefined {
  if (isHttpErrorInstance(error)) return error.status;
  if (error != null && typeof error === 'object' && 'response' in error) {
    return (error as { response?: { status?: number } }).response?.status;
  }
  return undefined;
}

/**
 * Extract response data from an HTTP error.
 * Works with both AxiosError (current) and HttpError (future).
 */
export function getHttpErrorData(error: unknown): unknown {
  if (isHttpErrorInstance(error)) return error.data;
  if (error != null && typeof error === 'object' && 'response' in error) {
    return (error as { response?: { data?: unknown } }).response?.data;
  }
  return undefined;
}

/**
 * Check if an error is an HTTP error with a response status.
 * Works with both AxiosError (current) and HttpError (future).
 */
export function isHttpError(error: unknown): boolean {
  return getHttpErrorStatus(error) !== undefined;
}

/**
 * Returns true for transient errors that are safe to retry (429, 408, 5xx).
 */
export function isRetryableHttpError(error: unknown): boolean {
  const status = getHttpErrorStatus(error);
  return (
    status === 429 || status === 408 || (status !== undefined && status >= 500 && status < 600)
  );
}

/**
 * Extract the `description` field from an HTTP error's response data.
 * The backend consistently returns `{ description: string }` in error responses.
 * Works with both AxiosError (current) and HttpError (future).
 */
export function getHttpErrorDescription(error: unknown): string | undefined {
  const data = getHttpErrorData(error);
  if (data != null && typeof data === 'object' && 'description' in data) {
    return typeof data.description === 'string' ? data.description : undefined;
  }
  return undefined;
}

/**
 * Extract an error message from inbox-automation error responses.
 *
 * Supports two shapes (new format preferred):
 *   new: { errors: [{ code: string; message: string }, ...] }
 *   old: { error: string }
 *
 * Returns `undefined` when neither shape matches.
 */
export function getHttpErrorMessage(error: unknown): string | undefined {
  const data = getHttpErrorData(error);
  if (data == null || typeof data !== 'object') return undefined;

  if ('errors' in data) {
    const { errors } = data as { errors: unknown };
    if (Array.isArray(errors) && errors.length > 0) {
      const first = errors[0];
      if (first != null && typeof first === 'object' && 'message' in first) {
        const { message } = first as { message: unknown };
        if (typeof message === 'string') return message;
      }
    }
  }

  if ('error' in data) {
    const { error: legacyError } = data as { error: unknown };
    if (typeof legacyError === 'string') return legacyError;
  }

  return undefined;
}
